import * as uuid from 'uuid';
import { cloneDeep } from 'lodash';

import { BriefingChartDisplayElementApiModel } from '../../../api/briefings/model/briefing-chart-display-element-api.model';
import { BriefingChartModel } from './briefing-chart.model';
import { ImageStyleModel } from './image-style.model';
import { BriefingChartImageDisplayElementModel } from './briefing-chart-image-display-element.model';
import { BriefingImageDisplayElementApiModel } from '../../../api/briefings/model/briefing-image-display-element-api.model';
import { BlockModel } from './block.model';
import { ChartDisplayElementModel } from '../../Reports/models/chart-display-element.model';
import {
    ReportBlockTypeEnum,
    reportBlockTypeEnumFromApi,
    reportBlockTypeEnumToApi,
    reportBlockTypeEnumToDefaultChartSpecificType
} from '../../Reports/enums/report-block-type.enum';
import { ReportSeriesModel } from '../../Reports/models/report-series.model';
import { BriefingSearchApi } from '../../../api/briefings/model/briefing-search-api';
import { mentionSourceApiTypesEnumToSeriesSourceTypesEnum, seriesSourceTypesEnumToMentionSourceApiTypesEnum } from '../../Reports/enums/series-type.enum';
import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';
import { reportBlockTypesMap } from '../../Reports/models/reports-block-types.model';
import { ChartStylesModel } from '../../Charts/models/chart-styles.model';
import { MapChartColorSchemeEnum } from '../../Charts/models/map-chart.model';
import { ChartDisplaySettingsModel } from '../../Charts/models/chart-display-settings.model';
import { SortableMentionBlockTypeEnum } from '../../Reports/enums/sortable-mention-block-type.enum';
import { ChartSpecificTypesEnum } from '../../Reports/enums/chart-specific-types.enum';
import { ChartBlockComponent } from '../../Reports/components/ChartBlock/chart-block.component';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { DimensionsModel } from '../../Misc/models/dimensions.model';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { ReportSeriesApiModel } from '../../../api/reports/model/report-series-api.model';
import { BlockDisplayTypeEnum } from '../enums/block-display-type.enum';
import { BriefingChartBlockSeriesApi } from '../../../api/briefings/model/briefing-chart-block-series-api';
import { ReportSeriesSearchResult } from '../../Reports/models/report-series-search-result';
import { CoverageMetricChartDisplaySettings } from '../../../Common/Charts/models/coverage-metric-chart-display-settings.model';
import { CoverageMetricChartDisplayElement } from '../../../Common/Reports/models/coverage-metric-chart-display-element.model';
import { CornerRadiusModel } from './corner-radius.model';
import { ChartAnnotations } from '@Common/Reports/models/chart-annotations.model';

/**
 * This display element model is unique, it behaves like an image display element but holds
 * a `chartBlock` property which contains the `displayElement: ChartDisplayElement` which is responsible for holding the necessary
 * properties to create a chart.model that's later converted into the image which is displayed.
 */
export class BriefingChartDisplayElementModel extends BriefingChartImageDisplayElementModel {

    placeholderHeight: number = 120;
    chart: BriefingChartModel;
    styles: ImageStyleModel;
    chartBlock: BlockModel;
    oldChartBlock: BlockModel;
    mentionSearches: MentionSearchModel[] = [];
    searches: BriefingSearchApi[] = [];
    useSearchDateFilter: boolean;

    constructor(
        apiModel?: BriefingChartDisplayElementApiModel,
    ) {
        super(apiModel as BriefingImageDisplayElementApiModel);

        if (apiModel) {
            this.chart = new BriefingChartModel(apiModel);
            this.styles = new ImageStyleModel(apiModel.styles);
            this.searches = apiModel.searches || [];
            this.useSearchDateFilter = apiModel.useSearchDateFilter;
            this.mapApiToReportChartBlock(apiModel);
        } else {
            this.chart = new BriefingChartModel();
            this.styles = new ImageStyleModel();

            this.styles.cornerRadius = new CornerRadiusModel(12, false);
        }
    }

    getSearchCriteria(): MentionSearchModel[] {
        return this.mentionSearches;
    }


    canRender(): boolean {
        if (this.displayType === BlockDisplayTypeEnum.TWIG) {
            return !!this.chart.chartUrl;
        } else {
            return !!this.chartBlock && !!this.chartBlock.displayElement && this.chartBlock.displayElement.canRender();
        }
    }

    toApi(): BriefingChartDisplayElementApiModel {
        const reportChartDisplayElement = this.chartBlock?.displayElement as ChartDisplayElementModel;
        reportChartDisplayElement?.series?.forEach(series => series.cacheResult = !this.chart?.generateOnSend);

        let stylesApi: any = null;
        try {
            stylesApi = this.styles?.toApi?.();
        } catch {
            // ok
        }

        let chartOptions = '';
        try {
            chartOptions = JSON.stringify(this.toChartOptionsApi()) || '';
        } catch {
            // ok
        }

        let searches: any[] = [];
        try {
            searches = this.toSearchesApi();
        } catch {
            // ok
        }

        let sources: any[] = [];
        try {
            sources = this.toSourcesApi();
        } catch {
            // ok
        }

        return Object.assign(
            {},
            <BriefingChartDisplayElementApiModel>{
                chartUrl: this.chart?.chartUrl,
                styles: stylesApi,
                generateOnSend: this.chart?.generateOnSend,
                chartOptions,
                showPreviousPeriod: reportChartDisplayElement?.chartHeaderSettings?.previousPeriod,
                searches,
                imageProperties: {
                    url: this.url ?? '',
                    width: this.width,
                    caption: this.settings?.caption,
                    altText: this.settings?.altText,
                    displaySize: this.settings?.displaySize,
                },
                sources,
                useSearchDateFilter: this.useSearchDateFilter,
            }
        );
    }

    clone(): BriefingChartDisplayElementModel {
        const apiModel = this.toApi();
        apiModel.searches?.forEach(item => item.searchReferenceId = uuid.v4());
        const model = new BriefingChartDisplayElementModel(apiModel) as BriefingChartDisplayElementModel;
        model.chart.widthPercent = this.chart.widthPercent;
        model.chart.heightPx = this.chart.heightPx;
        if (this.chartBlock) {
            model.chartBlock = this.chartBlock.clone();
            (this.chartBlock?.displayElement as ChartDisplayElementModel)?.series.forEach((s, i) => {
                const data = s.dataProvider.getValue();
                (model.chartBlock.displayElement as ChartDisplayElementModel).series[i].dataProvider.next(data);
            });
        }
        model.styles.styleState = this.styles.styleState;
        model.styles.displayStyleState = this.styles.displayStyleState;
        model.mentionSearches = (this.mentionSearches ?? []).map(m =>
            typeof (m as MentionSearchModel)?.clone === 'function' ? (m as MentionSearchModel).clone(false) : m
        );
        model.displayType = this.displayType;
        model.useSearchDateFilter = this.useSearchDateFilter;

        return model;
    }

    async onUpdate(): Promise<boolean> {
        let changes = false;
        if (this.chart.chartUrl !== this.chart.oldChartUrl) {
            changes = true;
        }
        if (this.chart?.chartUrl?.includes('agilitypr.media')) {
            this.chart.oldChartUrl = this.chart.chartUrl;
        }

        return changes;
    }

    isEqual(other: BriefingChartDisplayElementModel): boolean {

        if (!super.isEqual(other)) {
            return false;
        }

        if (!this.chart.isEqual(other.chart)) {
            return false;
        }

        if (this.useSearchDateFilter !== other.useSearchDateFilter) {
            return false;
        }

        if (this.isChartSetupChanged(
            this.chartBlock?.displayElement as ChartDisplayElementModel,
            other.chartBlock?.displayElement as ChartDisplayElementModel
        )) {
            return false;
        }

        return true;
    }

    toChartOptionsApi(): any {
        if (!this.chartBlock?.type?.key || !reportBlockTypeEnumToApi.has(this.chartBlock.type.key as ReportBlockTypeEnum)) {
            return {};
        }

        const chartDisplayElement = this.chartBlock.displayElement as ChartDisplayElementModel;

        // we have to clone it here, because then styles because pass by reference, and we keep adding to it
        const data = cloneDeep(chartDisplayElement.styles);
        data.metric = chartDisplayElement.metric;
        data.metrics = chartDisplayElement.metrics;
        data.chartSpecificType = chartDisplayElement.chartSpecificType;
        data.termType = chartDisplayElement.termType;
        data.numOfItems = chartDisplayElement.numOfItems;
        data.mapChartColorScheme = chartDisplayElement.mapChartColorScheme;
        data.mapChartChoroplethInterpolationType = chartDisplayElement.mapChartChoroplethInterpolationType;
        data.topMentionsSortField = chartDisplayElement.topMentionsSortField;
        data.topMentionsMetadata = chartDisplayElement.topMentionsMetadata;
        data.topCuratedMentions = chartDisplayElement.topCuratedMentions?.map(x => x.convertToApiMention());
        data.chartDisplaySettings = chartDisplayElement.chartHeaderSettings;
        data.chartHeaderSettings = data.chartDisplaySettings;
        data.timeInterval = chartDisplayElement.timeInterval;
        data.dateRangeLabelText = chartDisplayElement.dateRangeLabel;
        data.labelDisplayOptions = chartDisplayElement.labelDisplayOptions;
        data.enableDataDisplaySettings = chartDisplayElement.enableDataDisplaySettings;
        data.sentiments = chartDisplayElement.sentiments;
        data.networkSelection = chartDisplayElement.networkSelection;
        data.prominenceTrendGroup = chartDisplayElement.series[0]?.prominenceTrendGroup;
        data.stackedChartTypes = chartDisplayElement.stackedChartTypes;
        data.trackedColorChanged = chartDisplayElement.trackedColorChanged;
        data.legendPosition = chartDisplayElement.legendPosition;
        data.prevents = chartDisplayElement.prevents;
        data.chartAnnotations = chartDisplayElement.chartAnnotations?.clone();
        if (chartDisplayElement.series?.length === 1) {
            data.primarySeriesLabelText = chartDisplayElement.series[0].label;
        }
        if (this.chartBlock?.type?.key === ReportBlockTypeEnum.COVERAGE_METRIC) {
            data.titleIcon = (chartDisplayElement as CoverageMetricChartDisplayElement).titleIcon;
            data.customCurrentValue = (chartDisplayElement as CoverageMetricChartDisplayElement).customCurrentValue;
        }

        // there can only be one color associated with this series, so delete all other colors that
        // look like they are meant to associate with *a* series
        // we'll set the colors just below, per series
        for (const [key, _] of Object.entries(data.colorMap)) {
            if (key.startsWith(ReportSeriesModel.getSeriesColorKeyPrefix())) {
                delete data.colorMap[key];
            }
        }

        for (let i = 0; i < chartDisplayElement.series.length; i++) {
            const series = chartDisplayElement.series[i];
            const seriesKey = ReportSeriesModel.getSeriesColorKeyPrefix() + i;

            // actually set the color for the series in the colorMap, if appropriate
            if (series.color) {
                data.colorMap[seriesKey] = series.color;
            }
        }

        const chartOptions = {
            type: reportBlockTypeEnumToApi.get(this.chartBlock.type.key as ReportBlockTypeEnum),
            title: chartDisplayElement.title,
            width: this.chartBlock.cols,
            height: this.chartBlock.rows,
            data: data.toApi(),
        };

        return chartOptions;
    }

    toSearchesApi(): BriefingSearchApi[] {
        const chartDisplayElement = this.chartBlock?.displayElement as ChartDisplayElementModel;
        if (!chartDisplayElement?.series?.length) {
            return [];
        }

        const searches: BriefingSearchApi[] = [];
        chartDisplayElement.series.forEach((series, index) => {
            series.styles.label = series.label;
            series.styles.prominenceGroupId = series.prominenceTrendGroup?.id;
            series.styles.chartSpecificType = series.chartSpecificType;
            series.styles.externalDataSource = series.externalData;
            series.styles.dateRange = series.dateRange?.toApiModel();
            const seriesApi = series.toApiModel();

            searches.push({
                filterSearchCriteriaId: this.mentionSearches?.[index]?.id || this.searches?.[index]?.filterSearchCriteriaId,
                searchCriteriaId: seriesApi.sourceId,
                sourceType: seriesSourceTypesEnumToMentionSourceApiTypesEnum.get(seriesApi.sourceType),
                styles: seriesApi.styles,
                searchReferenceId: series.seriesUniqueUuid
            });
        });

        return searches;
    }

    toSourcesApi(): BriefingChartBlockSeriesApi[] {
        const chartDisplayElement = this.chartBlock?.displayElement as ChartDisplayElementModel;
        if (!chartDisplayElement?.series?.length) {
            return [];
        }

        const sources = chartDisplayElement.series.filter(x => x.cacheResult).map(series => {
            const seriesApi = series.toApiModel();
            return {
                searchReferenceId: series.seriesUniqueUuid,
                currentPeriodResults: series.searchResults?.currentPeriodResults,
                previousPeriodResults: series.hasPreviousPeriod ? series.searchResults?.previousPeriodResults : null,
                styles: seriesApi.styles,
            } as BriefingChartBlockSeriesApi;
        });

        return sources;
    }

    mapApiToReportChartBlock(apiModel: BriefingChartDisplayElementApiModel) {
        const chartOptions: any = JSON.parse(apiModel?.chartOptions) || '';

        if (!chartOptions) {
            return;
        }

        chartOptions.type = reportBlockTypeEnumFromApi.get(chartOptions?.type) || chartOptions?.type;
        const blockType = reportBlockTypesMap.get(chartOptions.type as ReportBlockTypeEnum);

        if (!blockType) {
            return;
        }

        const block: BlockModel = new BlockModel(
            null,
            blockType,
            new (blockType.displayElementType)()
        );

        const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
        const data = new ChartStylesModel(chartOptions.data);
        chartDisplayElement.metric = data.metric;
        chartDisplayElement.metrics = data.metrics;
        chartDisplayElement.sentiments = data.sentiments;
        chartDisplayElement.networkSelection = data.networkSelection;
        chartDisplayElement.labelDisplayOptions = data.labelDisplayOptions;
        chartDisplayElement.referralTypes = data.referralTypes;
        chartDisplayElement.prevents = data.prevents;
        chartDisplayElement.chartAnnotations = new ChartAnnotations(data.chartAnnotations?.indicators, data.chartAnnotations?.markerState);
        if (data.termChartType && !data.chartSpecificType) {
            // back compatibility
            chartDisplayElement.chartSpecificType = data.termChartType;
        } else {
            chartDisplayElement.chartSpecificType = data.chartSpecificType;
        }

        if (!chartDisplayElement.chartSpecificType) {
            chartDisplayElement.chartSpecificType = reportBlockTypeEnumToDefaultChartSpecificType.get(block.type.key as ReportBlockTypeEnum);
        }

        chartDisplayElement.termType = data.termType;
        chartDisplayElement.numOfItems = data.numOfItems;
        if (chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.MAP) {
            chartDisplayElement.mapChartColorScheme = data.mapChartColorScheme || MapChartColorSchemeEnum.REGION_COLORS;
        }
        chartDisplayElement.mapChartChoroplethInterpolationType = data.mapChartChoroplethInterpolationType;
        chartDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel(data.chartDisplaySettings);
        chartDisplayElement.timeInterval = data.timeInterval;
        chartDisplayElement.dateRangeLabel = data.dateRangeLabelText;
        chartDisplayElement.enableDataDisplaySettings = data.enableDataDisplaySettings;
        chartDisplayElement.stackedChartTypes = data.stackedChartTypes;
        chartDisplayElement.trackedColorChanged = data.trackedColorChanged;
        chartDisplayElement.legendPosition = data.legendPosition;

        // TODO add below back once we add mention charts to briefing
        // chartDisplayElement.topMentionsSortField = data.topMentionsSortField;
        // chartDisplayElement.topMentionsMetadata = new MentionCustomizationModel(data.topMentionsMetadata);
        // chartDisplayElement.topCuratedMentions = data.topCuratedMentions?.map(x => new Mention(x)) || [];

        if (chartOptions.type === ReportBlockTypeEnum.COVERAGE_METRIC) {
            chartDisplayElement.chartHeaderSettings = new CoverageMetricChartDisplaySettings(data.chartHeaderSettings);
            (chartDisplayElement as CoverageMetricChartDisplayElement).titleIcon = data.titleIcon;
            (chartDisplayElement as CoverageMetricChartDisplayElement).customCurrentValue = data.customCurrentValue;
        }

        chartDisplayElement.series = apiModel.searches.map(
            search => {
                const styles = JSON.parse(search.styles);
                const label = styles.label;
                const prominenceGroupId = styles.prominenceGroupId;
                const chartSpecificType = styles.chartSpecificType;
                const externalData = styles.externalDataSource;
                const dateRange = styles.dateRange;
                let prominenceTrendGroup = null;
                if (data.prominenceTrendGroup) {
                    prominenceTrendGroup = data.prominenceTrendGroup;
                }

                delete styles.prominenceGroupId;
                delete styles.externalDataSource;

                const series = new ReportSeriesModel(
                    {
                        filterId: search.filterSearchCriteriaId,
                        styles: JSON.stringify(styles),
                        sourceType: mentionSourceApiTypesEnumToSeriesSourceTypesEnum.get(search.sourceType),
                        sourceId: search.searchCriteriaId,
                        label,
                        prominenceGroupId,
                        prominenceTrendGroup,
                        chartSpecificType,
                        externalData,
                        dateRange,
                        id: null
                    } as ReportSeriesApiModel,
                    reportBlockTypeEnumFromApi.get(chartOptions.type) || chartOptions.type,
                    null,
                    data.metric,
                    chartDisplayElement
                );

                series.seriesUniqueUuid = search.searchReferenceId || uuid.v4();
                const result = apiModel.sources?.find(x => x.searchReferenceId === series.seriesUniqueUuid);
                if (result && !apiModel.generateOnSend) {
                    series.cacheResult = true;
                    series.searchResults = new ReportSeriesSearchResult();
                    series.searchResults.currentPeriodResults = result?.currentPeriodResults;
                    series.searchResults.previousPeriodResults = result?.previousPeriodResults;
                }

                return series;
            }
        );

        if (SortableMentionBlockTypeEnum[block.type.key]) {
            // top mentions custom only need mentions no facets, so it won't be retrieved for facet search
            if (chartDisplayElement.topMentionsSortField !== SearchCriteriaSortFieldEnum.custom) {
                chartDisplayElement.series.forEach(x => x.sortField = chartDisplayElement.topMentionsSortField);
            }
        }

        if (chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.WORD_CLOUD) {
            block.type = block.type.clone();
            block.type.componentType = ChartBlockComponent;
        }

        for (const series of chartDisplayElement.series) {
            if (data.sentiments) {
                series.sentiments = data.sentiments;
            }
        }

        // set the color property of each series, if it exists in the colormap
        for (let i = 0; i < chartDisplayElement.series.length; i++) {
            const color = data.colorMap[ReportSeriesModel.getSeriesColorKeyPrefix() + i];

            if (color) {
                chartDisplayElement.series[i].color = color;
            }
        }

        // if the data is older it won't contain options for sentiment or label display
        // provide the defaults here:
        if (data.labelDisplayOptions === undefined) {
            chartDisplayElement.labelDisplayOptions = [
                LabelDisplayOptionsEnum.percent
            ];
        }

        if (!data.sentiments) {
            chartDisplayElement.sentiments = [
                SearchCriteriaQuerySentimentsEnum.positive,
                SearchCriteriaQuerySentimentsEnum.neutral,
                SearchCriteriaQuerySentimentsEnum.negative,
                SearchCriteriaQuerySentimentsEnum.notAvailable
            ];
        }

        // label display options were briefly stored directly on the 'checkboxOptions' property instead
        // of on 'labelDisplayOptions' - this is a compatibility layer
        if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.percent)) {
            chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.percent);
            data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.percent), 1);
        }

        if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.count)) {
            chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.count);
            data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.count), 1);
        }

        delete data.sentiments;
        delete data.labelDisplayOptions;
        delete data.referralTypes;
        delete data.prevents;

        chartDisplayElement.styles = new ChartStylesModel(data);
        chartDisplayElement.title = chartOptions.title;
        chartDisplayElement.size = new DimensionsModel(block.rows, block.cols);
        block.displayElement = chartDisplayElement;

        this.chartBlock = block;
        this.oldChartBlock = block.clone();
    }


    private isChartSetupChanged(a: ChartDisplayElementModel, b: ChartDisplayElementModel): boolean {
        if (!a || !b) {
            if (!a && !b) {
                return false;
            }
            return true;
        }

        const x = a.clone();
        const y = b.clone();

        [x, y].forEach(z => {
            (z.series || []).forEach(s => {
                s.styles = this.removeUndefinedValues(s.styles);
                if (s.styles?.dateRange) {
                    s.styles.dateRange = this.removeUndefinedValues(s.styles.dateRange);
                }
            });
        });

        return !x.isEqual(y);
    }

    private removeUndefinedValues(x: any): any {
        if (x) {
            Object.keys(x).forEach(key => {
                if (x[key] === undefined) {
                    delete x[key];
                }
            });
        }

        return x;
    }
}
